import { useEffect, useState } from "react";
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';
import UserRole from "../../api/models/userrole";
import { Role, RoleIdLookup } from "../../common/constants";
import { hasValue } from '../../common/utils';
import BillingSpecialistDashboard from "./billingSpecialist";
import { DashboardProps } from "./types/dashboardProps.d";
import DirectorDashboard from "./director";
import TherapistDashboard from "./therapist";
import moment from "moment";
import DisciplineModal from "../disciplineModal";
import Discipline from "../../api/models/discipline";

const DefaultDashboard = ({ user }: DashboardProps): JSX.Element => {
  const [currentRole, setCurrentRole] = useState<UserRole>();
  const [currentRoleId, setCurrentRoleId] = useState<number>(0);
  const [discipline, setDiscipline] = useState<Discipline>();
  const [disciplineId, setDisciplineId] = useState<number>(-1);
  const [openDisciplineSelector, setOpenDiscipline] = useState(false);
  const [disciplineName, setDisciplineName] = useState<string>('');

  useEffect(() => {
    //Set the default role on initial render
    if (user?.userRoles !== null && user?.userRoles !== undefined) {
      const role: UserRole = user?.userRoles[0]!;
      setCurrentRole(role);
      setCurrentRoleId(role?.role?.roleId);
      handleRoleChange(role?.role?.roleId);

      //Get the first discipline this user has and then set it
      const disciplineIds: number[] = [];
      let disciplines: Discipline[] = [];
      user?.therapists?.map(x => {
        if (x && x.discipline && x.discipline.disciplineId) {
          disciplines.push(x.discipline);
          disciplineIds.push(x.discipline.disciplineId);
        }
      });
      disciplines = disciplines.sort((a, b) => a.name.localeCompare(b.name));

      let first: Discipline = disciplines[0];
      if (first && first.disciplineId !== undefined) {
        setDiscipline(first);
        setDisciplineId(first.disciplineId);

        if (first.name && first.name !== '') {
          setDisciplineName(first.name);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleRoleChange = (roleId: number) => {
    setCurrentRoleId(roleId);
    setCurrentRole(user.userRoles.filter((r) => r?.role?.roleId === roleId)[0]);
  }

  useEffect(() => {
    if (discipline?.disciplineId && discipline?.disciplineId > 0) {
      setDisciplineId(discipline?.disciplineId!);
    }

    if (discipline && discipline.name && discipline.name !== '') {
      setDisciplineName(discipline.name);
    }
  }, [discipline]);

  return (
    <div className="dashboard defaultDashboard">
      {user !== undefined && (
        <>
          {user?.userRoles !== undefined &&
            user?.userRoles !== null &&
            user.userRoles.filter((r) => r?.role?.roleId === RoleIdLookup.Therapist).length > 0 &&
            <DisciplineModal
              open={openDisciplineSelector}
              toggle={setOpenDiscipline}
              disciplineId={disciplineId!}
              setDiscipline={(d) => setDiscipline(d)}
              noInitialChange
            />
          }
          <div className="grid w-full grid-cols-2">
            <div className="col-span-1">
              {/* Welcome Message */}
              {hasValue(user) && (
                <>
                  <div>Name: {`${user.firstName} ${user.lastName}`}</div>
                  <div>{moment(new Date()).format('LLL')}</div>
                </>
              )}
            </div>

            {/* User Role Selector */}
            <div className={`col-span-1 float-right mr-6 roles-${user?.userRoles.length ?? 0}`} key={`userRoles-${user?.userRoles.length ?? 0}`}>
              {user?.userRoles !== undefined && user?.userRoles !== null && (
                <>
                  {user?.userRoles.length > 1 && (
                    <div>
                      <Select
                        key={`dashboard-selector-${currentRoleId}`}
                        className="block mt-1"
                        value={user.userRoles
                          .map(role => ({ value: role.role.roleId, label: role.role.name }))
                          .find(o => o.value === currentRoleId) || null}
                        onChange={(selected) => selected && handleRoleChange(Number(selected.value))}
                        options={user.userRoles
                          .sort((a, b) => a.role.name.localeCompare(b.role.name))
                          .map((role) => ({ value: role.role.roleId, label: role.role.name }))}
                        placeholder='Please select a dashboard...'
                        styles={graySelectStyles}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Discipline Picker */}
          {user?.userRoles !== undefined &&
            user?.userRoles !== null &&
            user.userRoles.filter((r) => r?.role?.roleId === RoleIdLookup.Therapist).length > 0 &&
            <div onClick={() => { setOpenDiscipline(true); }}>
              <span className='font-bold'>{disciplineName}</span>
              <button className='block text-sm link'>
                Change Discipline
              </button>
            </div>
          }

          {/* Dashboards */}
          <div className="mt-6 dashboards-container">
            {hasValue(user) && currentRole?.role.name === Role.Therapist && <TherapistDashboard user={user} discipline={discipline} />}
            {hasValue(user) && currentRole?.role.name === Role.Director && <DirectorDashboard user={user} discipline={discipline} />}
            {hasValue(user) && currentRole?.role.name === Role.BillingSpecialist && <BillingSpecialistDashboard user={user} discipline={discipline} />}
          </div>
        </>
      )
      }
    </div >
  );
};

export default DefaultDashboard;
